import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Loader2, AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ModalShell } from '@/components/ModalShell';
import { useRecentCompleted, type RecentCompleted } from '@/features/agent-metrics/useRecentCompleted';
import { ScreeningReviewModal } from '@/features/screening/ScreeningReviewModal';
import { formatRelative } from '@/lib/format';
import { useT } from '@/i18n';
import { cn } from '@/lib/utils';

/**
 * F5-R1a (encargo 4) — «Completed screenings» al brand Y por i18n ES/EN
 * (estaba clavado en inglés). Misma lógica, mismos datos, mismo review modal;
 * solo el vestido (paleta nav-*) y las etiquetas por t().
 */
interface CompletedListModalProps {
  open: boolean;
  onClose: () => void;
}

type Filter = 'all' | 'needs_nav' | 'no_findings';

export function CompletedListModal({ open, onClose }: CompletedListModalProps): JSX.Element | null {
  const t = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const completedQ = useRecentCompleted(100);
  const [filter, setFilter] = useState<Filter>('all');
  const [reviewId, setReviewId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <>
      <ModalShell open={open} onClose={onClose} ariaLabelledBy="completed-modal-title">
        <header className="sticky top-0 bg-white border-b border-nav-hair px-6 py-5 flex items-center justify-between rounded-t-[14px] z-10">
          <h2 id="completed-modal-title" className="font-head text-xl font-bold text-nav-navy tracking-tight">
            {t('agentF5.r1a.cl.title')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('common.close')}>
            <X className="h-4 w-4" />
          </Button>
        </header>

        <div className="modal-scroll flex-1 overflow-y-auto font-body text-nav-navy">
          <div
            ref={dialogRef}
            tabIndex={-1}
            className="px-6 py-4"
          >
            <div className="border-b border-nav-hair pb-4 mb-4">
              <FilterTabs value={filter} onChange={setFilter} />
            </div>

            {completedQ.isLoading && (
              <div className="text-center py-8">
                <Loader2 className="h-5 w-5 animate-spin mx-auto text-nav-teal" />
              </div>
            )}

            {completedQ.error && (
              <Alert variant="error">
                <AlertTitle>{t('agentF5.r1a.cl.errLoad')}</AlertTitle>
                <AlertDescription>{(completedQ.error as Error).message}</AlertDescription>
              </Alert>
            )}

            {completedQ.data && (
              <CompletedRows
                rows={completedQ.data}
                filter={filter}
                onSelect={(id) => setReviewId(id)}
              />
            )}
          </div>
        </div>
      </ModalShell>

      {reviewId && (
        <ScreeningReviewModal screeningId={reviewId} onClose={() => setReviewId(null)} />
      )}
    </>
  );
}

function FilterTabs({
  value,
  onChange,
}: {
  value: Filter;
  onChange: (v: Filter) => void;
}): JSX.Element {
  const t = useT();
  // F5-R1a — clases ESTÁTICAS del brand (las `bg-${tone}-100` dinámicas de
  // antes ni siquiera compilaban en Tailwind) + etiquetas por i18n.
  const tabs: Array<{ key: Filter; label: string; activeCls: string }> = [
    { key: 'all', label: t('agentF5.r1a.cl.tabAll'), activeCls: 'bg-nav-tealTint text-nav-tealDark' },
    { key: 'needs_nav', label: t('agentF5.r1a.cl.tabNeedsNav'), activeCls: 'bg-nav-amberTint text-nav-amberText' },
    { key: 'no_findings', label: t('agentF5.r1a.cl.tabNoFindings'), activeCls: 'bg-nav-sageTint text-nav-sageText' },
  ];
  return (
    <div role="tablist" className="inline-flex rounded-[10px] border-[1.5px] border-nav-card bg-white overflow-hidden">
      {tabs.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={value === tab.key}
          onClick={() => onChange(tab.key)}
          className={cn(
            'px-3 min-h-[44px] font-head text-sm font-semibold transition-colors',
            value === tab.key ? tab.activeCls : 'text-nav-slate hover:bg-nav-soft',
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function CompletedRows({
  rows,
  filter,
  onSelect,
}: {
  rows: RecentCompleted[];
  filter: Filter;
  onSelect: (id: string) => void;
}): JSX.Element {
  const t = useT();
  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'needs_nav') return rows.filter((r) => r.positiveFindingsCount > 0);
    if (filter === 'no_findings') return rows.filter((r) => r.positiveFindingsCount === 0);
    return rows;
  }, [rows, filter]);

  if (filtered.length === 0) {
    return (
      <p className="text-sm text-nav-slate text-center py-8">
        {t('agentF5.r1a.cl.empty')}
      </p>
    );
  }

  return (
    <div className="border-[1.5px] border-nav-card rounded-[10px] overflow-hidden divide-y divide-nav-hair bg-white">
      {filtered.map((c) => {
        const needsNav = c.positiveFindingsCount > 0;
        const safetyAlert = c.safetyConcernFlag;
        return (
          <button
            key={c.screeningId}
            type="button"
            onClick={() => onSelect(c.screeningId)}
            className={cn(
              'w-full text-left flex items-center gap-3 px-3 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
              safetyAlert
                ? 'hover:bg-nav-redTint bg-nav-redTint/40'
                : needsNav
                  ? 'hover:bg-nav-amberTint'
                  : 'hover:bg-nav-softer',
            )}
          >
            <span className="text-xs text-nav-slate tabular-nums w-20 shrink-0">
              {c.submittedAt
                ? new Date(c.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                : '—'}
            </span>
            <span className="font-mono text-xs text-nav-navy truncate w-24 shrink-0">…{c.cinHash.slice(-6)}</span>
            <span className="text-nav-navy tabular-nums w-20 shrink-0">
              {c.totalDurationSec != null
                ? `${Math.floor(c.totalDurationSec / 60)}m ${String(c.totalDurationSec % 60).padStart(2, '0')}s`
                : '—'}
            </span>
            {c.timeCategory && (
              <span className="font-head text-[11px] font-bold px-2 py-0.5 rounded-full bg-nav-sageTint text-nav-sageText border border-nav-sage shrink-0">
                {c.timeCategory.replace('_', '-')}
              </span>
            )}
            {needsNav && (
              <span
                className={cn(
                  'font-head text-[11px] font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 shrink-0',
                  safetyAlert
                    ? 'bg-nav-redTint text-nav-red border-nav-red'
                    : 'bg-nav-amberTint text-nav-amberText border-nav-amber',
                )}
                title={safetyAlert ? t('agentF5.r1a.cl.safetyFlagged') : t('agentF5.r1a.cl.hasFindings')}
              >
                <AlertTriangle className="h-3 w-3" />
                {c.positiveFindingsCount === 1
                  ? t('agentF5.r1a.cl.findingOne')
                  : t('agentF5.r1a.cl.findingMany', { n: c.positiveFindingsCount })}
              </span>
            )}
            {!needsNav && (
              <span className="font-head text-[11px] font-bold px-2 py-0.5 rounded-full bg-nav-sageTint text-nav-sageText border border-nav-sage inline-flex items-center gap-1 shrink-0">
                <CheckCircle2 className="h-3 w-3" />
                {t('agentF5.r1a.cl.clearChip')}
              </span>
            )}
            <span className="text-nav-slate text-xs ml-auto">{formatRelative(c.submittedAt)}</span>
            <ChevronRight className="h-4 w-4 text-nav-faint shrink-0" />
          </button>
        );
      })}
    </div>
  );
}
